import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminRegionalSettings, saveAdminRegionalSettings } from '../../api/regionalSettings';
import { useRegionalSettings } from '../../context/RegionalSettingsContext';
import { REGION_PRESETS, regionalPresetPayload, getRegionalPresetLabel } from '../../config/regionalPresets';
import { defaultDatetimeFormatForTimeFormat, formatTimeFromHhMm, getAcademyTimeZone } from '../../utils/regionalFormat';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { TextInput, SelectInput, CheckboxField } from '../../components/common/form';
import { UI_LANGUAGES, serializeEnabledUiLanguages } from '../../utils/uiLanguage';

const ADMIN_PRESET_KEYS = [
    'india', 'india12', 'uae', 'us', 'uk',
    'eurozone', 'germany', 'france', 'netherlands', 'belgium', 'spain',
    'custom',
];

const TIMEZONES = [
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Colombo',
    'Asia/Dhaka', 'Asia/Kathmandu', 'Asia/Tokyo',
    'America/New_York', 'America/Chicago', 'America/Los_Angeles',
    'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Amsterdam',
    'Europe/Madrid', 'Europe/Rome', 'Europe/Warsaw', 'Europe/Brussels',
    'Europe/Stockholm', 'Europe/Vienna', 'Europe/Lisbon', 'Europe/Athens',
    'Europe/Dublin', 'Europe/Zurich',
    'Australia/Sydney', 'UTC',
];

function parseEnabledUiLanguages(raw) {
    const out = [];
    const seen = new Set();
    const push = (code) => {
        const c = String(code || '').trim().toLowerCase().slice(0, 2);
        if (!UI_LANGUAGES.includes(c) || seen.has(c)) return;
        seen.add(c);
        out.push(c);
    };
    push('en');
    if (Array.isArray(raw)) raw.forEach(push);
    else if (typeof raw === 'string') raw.split(/[,\s]+/).forEach(push);
    return out.length ? out : ['en'];
}

function withUiLanguageFields(data) {
    const enabledUiLanguages = parseEnabledUiLanguages(
        data?.enabledUiLanguages ?? data?.enabled_ui_languages,
    );
    let defaultUiLanguage = String(data?.defaultUiLanguage || data?.default_ui_language || 'en')
        .trim().toLowerCase().slice(0, 2);
    if (!enabledUiLanguages.includes(defaultUiLanguage)) {
        defaultUiLanguage = enabledUiLanguages[0] || 'en';
    }
    return { ...data, enabledUiLanguages, defaultUiLanguage };
}

export default function RegionalSettings() {
    const { t } = useTranslation();
    const { refresh } = useRegionalSettings();
    const [form, setForm] = useState(null);
    const [preset, setPreset] = useState('custom');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        getAdminRegionalSettings()
            .then((data) => {
                setForm(withUiLanguageFields(data));
                for (const key of ADMIN_PRESET_KEYS) {
                    if (key === 'custom') continue;
                    const p = REGION_PRESETS[key];
                    if (p.countryCode === data.countryCode && p.timezone === data.timezone
                        && p.timeFormat === data.timeFormat) {
                        setPreset(key);
                        return;
                    }
                }
                setPreset('custom');
            })
            .catch(() => setError(t('settings.regional.loadFailed')));
    }, []);

    function applyPreset(key) {
        setPreset(key);
        if (key !== 'custom') {
            setForm((prev) => ({ ...prev, ...regionalPresetPayload(key) }));
        }
    }

    function set(field, value) {
        setForm(prev => {
            const next = { ...prev, [field]: value };
            if (field === 'timeFormat') {
                next.datetimeFormat = defaultDatetimeFormatForTimeFormat(value, prev?.dateFormat);
            }
            return next;
        });
    }

    function sampleTime() {
        if (!form) return '—';
        return formatTimeFromHhMm('14:30', form.timeFormat || 'HOUR_24');
    }

    function sampleAmount() {
        if (!form) return '';
        try {
            const num = new Intl.NumberFormat(form.localeTag || 'en-IN', {
                minimumFractionDigits: form.currencyDecimals ?? 2,
                maximumFractionDigits: form.currencyDecimals ?? 2,
            }).format(1234.5);
            return form.currencySymbolBefore ? `${form.currencySymbol}${num}` : `${num} ${form.currencySymbol}`;
        } catch { return '—'; }
    }

    function sampleDate() {
        if (!form) return '';
        try {
            return new Intl.DateTimeFormat(form.localeTag || 'en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                timeZone: form.timezone || getAcademyTimeZone(),
            }).format(new Date());
        } catch { return '—'; }
    }

    function toggleUiLanguage(code, on) {
        if (code === 'en') return;
        setForm((prev) => {
            const current = parseEnabledUiLanguages(prev.enabledUiLanguages);
            let next = on
                ? [...current, code]
                : current.filter((c) => c !== code);
            next = parseEnabledUiLanguages(next);
            let defaultUiLanguage = prev.defaultUiLanguage || 'en';
            if (!next.includes(defaultUiLanguage)) {
                defaultUiLanguage = next[0] || 'en';
            }
            return { ...prev, enabledUiLanguages: next, defaultUiLanguage };
        });
    }

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setSuccess('');
        setError('');
        try {
            const enabled = parseEnabledUiLanguages(form.enabledUiLanguages);
            const defaultUiLanguage = enabled.includes(form.defaultUiLanguage)
                ? form.defaultUiLanguage
                : enabled[0];
            const payload = {
                ...form,
                enabledUiLanguages: serializeEnabledUiLanguages(enabled),
                defaultUiLanguage,
            };
            const saved = await saveAdminRegionalSettings(payload);
            setForm(withUiLanguageFields(saved));
            await refresh();
            setSuccess(t('settings.regional.saveSuccess'));
        } catch {
            setError(t('settings.regional.saveFailed'));
        } finally {
            setSaving(false);
        }
    }

    if (!form) {
        if (error) {
            return (
                <div className="max-w-3xl mx-auto p-6">
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                </div>
            );
        }
        return (
            <PageLoadingShell
                variant="page-shell"
                title={t('settings.regional.title')}
                subtitle={t('settings.regional.subtitle')}
            />
        );
    }

    return (
        <div className="max-w-3xl mx-auto p-6 space-y-6">
            <PageHeaderSection
                eyebrow={t('nav.system')}
                title={t('settings.regional.title')}
                subtitle={t('settings.regional.subtitle')}
            />

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <p className="text-sm font-semibold text-slate-700 mb-3">{t('settings.regional.quickPreset')}</p>
                <div className="flex flex-wrap gap-2">
                    {ADMIN_PRESET_KEYS.map((key) => {
                        const label = key === 'custom' ? t('settings.regional.custom') : getRegionalPresetLabel(key);
                        return (
                        <button key={key} type="button" onClick={() => applyPreset(key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${preset === key
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-slate-700 border-slate-300 hover:border-primary-400'
                                }`}>
                            {label}
                        </button>
                        );
                    })}
                </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex flex-wrap gap-6 text-sm">
                <div>
                    <span className="text-indigo-500 font-medium">{t('settings.regional.sampleAmount')} </span>
                    <span className="font-bold text-indigo-900">{sampleAmount()}</span>
                </div>
                <div>
                    <span className="text-indigo-500 font-medium">{t('settings.regional.sampleDate')} </span>
                    <span className="font-bold text-indigo-900">{sampleDate()}</span>
                </div>
                <div>
                    <span className="text-indigo-500 font-medium">{t('settings.regional.sampleTime')} </span>
                    <span className="font-bold text-indigo-900">{sampleTime()}</span>
                </div>
                <div>
                    <span className="text-indigo-500 font-medium">{t('settings.regional.timezoneLabel')} </span>
                    <span className="font-bold text-indigo-900">{form.timezone}</span>
                </div>
            </div>

            <form onSubmit={handleSave} className="space-y-5">
                <Card title={t('settings.regional.cardUiLanguages', { defaultValue: 'App languages' })}>
                    <p className="text-xs text-slate-500 -mt-2 mb-3">
                        {t('settings.regional.uiLanguagesHint', {
                            defaultValue: 'Choose which languages appear in the Header and profile. English is always available. If only one language is enabled, the language dropdown is hidden.',
                        })}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {UI_LANGUAGES.map((code) => {
                            const enabled = parseEnabledUiLanguages(form.enabledUiLanguages).includes(code);
                            return (
                                <CheckboxField
                                    key={code}
                                    layout="toggle-card"
                                    checked={enabled}
                                    disabled={code === 'en'}
                                    onChange={(e) => toggleUiLanguage(code, e.target.checked)}
                                    label={t(`pages.profile.lang_${code}`)}
                                    description={code === 'en'
                                        ? t('settings.regional.englishAlwaysOn', { defaultValue: 'Always enabled' })
                                        : undefined}
                                />
                            );
                        })}
                    </div>
                    <div className="mt-4">
                        <SelectInput
                            label={t('settings.regional.defaultUiLanguage', { defaultValue: 'Default language' })}
                            value={form.defaultUiLanguage || 'en'}
                            onChange={(e) => set('defaultUiLanguage', e.target.value)}
                            note={t('settings.regional.defaultUiLanguageNote', {
                                defaultValue: 'Used for new users and when a removed language must fall back.',
                            })}
                        >
                            {parseEnabledUiLanguages(form.enabledUiLanguages).map((code) => (
                                <option key={code} value={code}>{t(`pages.profile.lang_${code}`)}</option>
                            ))}
                        </SelectInput>
                    </div>
                    {parseEnabledUiLanguages(form.enabledUiLanguages).some((c) => c !== 'en') && (
                        <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            {t('settings.regional.whatsappLocaleWarn', {
                                defaultValue: 'If WhatsApp is enabled, register Meta/WATI templates with _de / _fr / _nl suffixes for each extra language you turn on.',
                            })}
                        </p>
                    )}
                </Card>

                <Card title={t('settings.regional.cardLocale')}>
                    <div className="grid grid-cols-2 gap-4">
                        <SelectInput
                            label={t('settings.regional.timezone')}
                            value={form.timezone || ''}
                            onChange={e => set('timezone', e.target.value)}
                            note={t('settings.regional.timezoneNote')}
                        >
                            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                            {!TIMEZONES.includes(form.timezone) && (
                                <option value={form.timezone}>{form.timezone}</option>
                            )}
                        </SelectInput>
                        <TextInput
                            label={t('settings.regional.localeTag')}
                            value={form.localeTag || ''}
                            onChange={e => set('localeTag', e.target.value)}
                            placeholder={t('settings.regional.placeholders.localeTag')}
                            note={t('settings.regional.localeTagNote')}
                        />
                    </div>
                </Card>

                <Card title={t('settings.regional.cardCountry')}>
                    <div className="grid grid-cols-2 gap-4">
                        <TextInput
                            label={t('settings.regional.countryCode')}
                            value={form.countryCode || ''}
                            onChange={e => set('countryCode', e.target.value.toUpperCase())}
                            maxLength={2}
                            placeholder={t('settings.regional.placeholders.countryCode')}
                        />
                        <TextInput
                            label={t('settings.regional.countryName')}
                            value={form.countryName || ''}
                            onChange={e => set('countryName', e.target.value)}
                            placeholder={t('settings.regional.placeholders.countryName')}
                        />
                        <TextInput
                            label={t('settings.regional.phoneDialCode')}
                            value={form.phoneDialCode || ''}
                            onChange={e => set('phoneDialCode', e.target.value)}
                            placeholder={t('settings.regional.placeholders.phoneDialCode')}
                        />
                        <TextInput
                            label={t('settings.regional.phoneFormatHint')}
                            value={form.phoneFormatHint || ''}
                            onChange={e => set('phoneFormatHint', e.target.value)}
                            placeholder={t('settings.regional.placeholders.phoneFormatHint')}
                        />
                    </div>
                    <p className="text-xs text-slate-500">
                        {t('settings.regional.countryNote')}
                    </p>
                </Card>

                <Card title={t('settings.regional.cardCurrency')}>
                    <p className="text-xs text-slate-500 -mt-2">
                        {t('settings.regional.currencyNote')}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                        <TextInput
                            label={t('settings.regional.currencyCode')}
                            value={form.currencyCode || ''}
                            onChange={e => set('currencyCode', e.target.value.toUpperCase())}
                            maxLength={3}
                            placeholder={t('settings.regional.placeholders.currencyCode')}
                        />
                        <TextInput
                            label={t('settings.regional.currencySymbol')}
                            value={form.currencySymbol || ''}
                            onChange={e => set('currencySymbol', e.target.value)}
                            placeholder={t('settings.regional.placeholders.currencySymbol')}
                        />
                        <TextInput
                            label={t('settings.regional.decimalPlaces')}
                            type="number"
                            min={0}
                            max={4}
                            value={form.currencyDecimals ?? 2}
                            onChange={e => set('currencyDecimals', parseInt(e.target.value, 10))}
                        />
                        <SelectInput
                            label={t('settings.regional.symbolPosition')}
                            value={form.currencySymbolBefore ? 'before' : 'after'}
                            onChange={e => set('currencySymbolBefore', e.target.value === 'before')}
                        >
                            <option value="before">
                                {t('settings.regional.symbolBefore', {
                                    example: form.currencySymbol
                                        ? `${form.currencySymbol}100`
                                        : '100',
                                })}
                            </option>
                            <option value="after">
                                {t('settings.regional.symbolAfter', {
                                    example: form.currencySymbol
                                        ? `100 ${form.currencySymbol}`
                                        : '100',
                                })}
                            </option>
                        </SelectInput>
                    </div>
                </Card>

                {(form.taxIdLabel || form.taxLabel) && (
                    <Card title={t('settings.regional.cardTax')}>
                        <p className="text-xs text-slate-500 -mt-2">
                            {t('settings.regional.taxNote')}
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <TextInput
                                label={t('settings.regional.taxLabel')}
                                value={form.taxLabel || ''}
                                onChange={e => set('taxLabel', e.target.value)}
                                placeholder={t('settings.regional.placeholders.taxLabel')}
                            />
                            <TextInput
                                label={t('settings.regional.taxRate')}
                                type="number"
                                step="0.01"
                                min={0}
                                value={form.taxRatePercent ?? ''}
                                onChange={e => set('taxRatePercent', e.target.value === '' ? null : parseFloat(e.target.value))}
                            />
                            {form.taxIdLabel ? (
                                <>
                                    <TextInput
                                        label={form.taxIdLabel || t('settings.regional.taxId')}
                                        value={form.taxRegistrationNumber || ''}
                                        onChange={e => set('taxRegistrationNumber', e.target.value)}
                                        placeholder={t('settings.regional.placeholders.taxRegistration', { label: form.taxIdLabel })}
                                    />
                                    <TextInput
                                        label={t('settings.regional.taxIdLabel')}
                                        value={form.taxIdLabel || ''}
                                        onChange={e => set('taxIdLabel', e.target.value)}
                                        placeholder={t('settings.regional.placeholders.taxIdLabel')}
                                    />
                                </>
                            ) : null}
                            <SelectInput
                                label={t('settings.regional.taxIncluded')}
                                value={form.taxIncludedInPrice ? 'yes' : 'no'}
                                onChange={e => set('taxIncludedInPrice', e.target.value === 'yes')}
                            >
                                <option value="yes">{t('settings.regional.taxIncludedYes')}</option>
                                <option value="no">{t('settings.regional.taxIncludedNo')}</option>
                            </SelectInput>
                        </div>
                    </Card>
                )}

                <Card title={t('settings.regional.cardDateTime')}>
                    <div className="grid grid-cols-2 gap-4">
                        <SelectInput
                            label={t('settings.regional.timeFormat')}
                            value={form.timeFormat || 'HOUR_24'}
                            onChange={e => set('timeFormat', e.target.value)}
                            note={t('settings.regional.timeFormatNote')}
                        >
                            <option value="HOUR_24">{t('settings.regional.time24')}</option>
                            <option value="HOUR_12">{t('settings.regional.time12')}</option>
                        </SelectInput>
                        <TextInput
                            label={t('settings.regional.datetimeFormat')}
                            value={form.datetimeFormat || ''}
                            onChange={e => set('datetimeFormat', e.target.value)}
                            placeholder={defaultDatetimeFormatForTimeFormat(form.timeFormat, form.dateFormat)}
                            note={t('settings.regional.datetimeFormatNote')}
                        />
                    </div>
                </Card>

                {success && (
                    <div className="rounded-lg bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm">
                        {success}
                    </div>
                )}
                {error && (
                    <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                        {error}
                    </div>
                )}

                <div className="flex justify-end">
                    <button type="submit" disabled={saving}
                        className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
                        {saving ? t('settings.regional.saving') : t('settings.regional.saveSettings')}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
            {children}
        </div>
    );
}
